import type {
  Assignment,
  Conflict,
  Shift,
  StaffingEstimate,
  Workspace,
  Unit,
  ServiceDefinition,
  Person,
  RuleOccurrence,
  RuleDefinition,
  AssignmentAllocation,
} from "./model";
import { activeAbsence } from './absence';
import { cycleForAssignment, currentFulfillment } from './stay-cycles';
import { resolveEstimate } from './estimates';
import {
  durationMinutes,
  intervalMinutes,
  localDate,
  localToInstant,
  weekday,
  overlap,
  operationalNow,
  stableId,
} from "./time";

/** Index one read of a workspace, rather than repeatedly scanning all demand for each row.
 * The caller owns its lifetime (for example, useMemo by workspace identity). Never reuse it
 * after mutating that workspace; command handlers deliberately use the uncached selectors.
 */
export interface AssignmentReadIndex {
  readonly state: Workspace;
  readonly assignments: ReadonlyMap<string, Assignment>;
  readonly units: ReadonlyMap<string, Unit>;
  readonly services: ReadonlyMap<string, ServiceDefinition>;
  readonly occurrences: ReadonlyMap<string, { occurrence: RuleOccurrence; order: number }>;
  readonly rules: ReadonlyMap<string, RuleDefinition>;
  readonly currentRequirements: ReadonlyMap<string, Assignment>;
  readonly committed: ReadonlyMap<string, AssignmentAllocation>;
}
function firstById<T extends { id: string }>(records: T[]): Map<string, T> {
  const result = new Map<string, T>();
  for (const record of records) if (!result.has(record.id)) result.set(record.id, record);
  return result;
}
function ruleRevisionKey(id: string, revision: number) { return JSON.stringify([id, revision]); }
export function createAssignmentReadIndex(state: Workspace): AssignmentReadIndex {
  const rules = new Map<string, RuleDefinition>();
  for (const rule of [...state.rules, ...(state.ruleHistory ?? [])]) {
    const key = ruleRevisionKey(rule.id, rule.revision);
    if (!rules.has(key)) rules.set(key, rule);
  }
  const currentRequirements = new Map<string, Assignment>();
  const revisions = new Map<string, number>();
  for (const impact of state.impacts) {
    if (!impact.assignmentId || impact.action !== 'update-window' || impact.status === 'superseded' || !impact.proposedAssignment) continue;
    // A stable descending sort used to choose the first item for equal revisions.
    if (!revisions.has(impact.assignmentId) || impact.sourceRevision > revisions.get(impact.assignmentId)!) {
      revisions.set(impact.assignmentId, impact.sourceRevision);
      currentRequirements.set(impact.assignmentId, impact.proposedAssignment);
    }
  }
  const committed = new Map<string, AssignmentAllocation>();
  for (const allocation of state.allocations)
    if (allocation.status === 'committed' && !committed.has(allocation.assignmentId)) committed.set(allocation.assignmentId, allocation);
  return {
    state, assignments: firstById(state.assignments), units: firstById(state.units), services: firstById(state.services),
    occurrences: new Map(state.occurrences.map((occurrence, order) => [occurrence.id, { occurrence, order }])),
    rules, currentRequirements, committed,
  };
}
function indexedOccurrences(state: Workspace, assignment: Assignment, index?: AssignmentReadIndex): RuleOccurrence[] {
  if (index?.state !== state) return state.occurrences.filter(item => assignment.occurrenceIds.includes(item.id) && !['superseded', 'suppressed'].includes(item.status));
  return [...new Set(assignment.occurrenceIds)]
    .flatMap(id => { const found = index.occurrences.get(id); return found && !['superseded', 'suppressed'].includes(found.occurrence.status) ? [found] : []; })
    .sort((a, b) => a.order - b.order)
    .map(item => item.occurrence);
}

export function currentAttendance(
  state: Workspace,
  personId: string,
  shiftId?: string,
  at = operationalNow(state),
): "expected" | "confirmed" | "present" | "absent" {
  if(activeAbsence(state,personId,at))return 'absent';
  const shift = shiftId
    ? state.shifts.find((item) => item.id === shiftId)
    : undefined;
  const timeZone =
    state.properties.find((item) => item.id === shift?.propertyId)?.timeZone ??
    "Europe/Helsinki";
  const date = shift?.date ?? localDate(at, timeZone);
  let latest: Workspace['attendance'][number] | undefined;
  for (const item of state.attendance) {
    if (item.personId !== personId || item.at > at || (item.effectiveEnd && item.effectiveEnd <= at)) continue;
    if (item.shiftId ? item.shiftId !== shiftId : localDate(item.at, timeZone) !== date) continue;
    // Frozen operational clocks legitimately produce equal timestamps. Later
    // recorded facts take precedence without rewriting the earlier event.
    if (!latest || item.at >= latest.at) latest = item;
  }
  return latest?.status ?? 'expected';
}
/** Union work first, then subtract only blocked portions intersecting that union. */
function netIntervalMinutes(
  start: string,
  end: string,
  working: { start: string; end: string }[],
  blocked: { start: string; end: string }[],
): number {
  if (start >= end) return 0;
  const cutouts = working.flatMap(work => blocked
    .filter(item => overlap(work.start, work.end, item.start, item.end))
    .map(item => ({ start: work.start > item.start ? work.start : item.start, end: work.end < item.end ? work.end : item.end })));
  return Math.max(0, intervalMinutes(start, end, working) - intervalMinutes(start, end, cutouts));
}
export function availableMinutes(
  state: Workspace,
  personId: string,
  start: string,
  end: string,
): number {
  const intervals = state.availability.filter(item => item.personId === personId);
  return netIntervalMinutes(start, end,
    intervals.filter(item => item.kind !== "unavailable"),
    intervals.filter(item => item.kind === "unavailable"),
  );
}
export function committedAllocation(state: Workspace, assignmentId: string, index?: AssignmentReadIndex) {
  return index?.state === state ? index.committed.get(assignmentId) : state.allocations.find(
    (item) => item.assignmentId === assignmentId && item.status === "committed",
  );
}
export function assignmentParticipants(
  state: Workspace,
  assignmentId: string,
): string[] {
  return committedAllocation(state, assignmentId)?.participantIds ?? [];
}
export function actualMinutes(
  state: Workspace,
  assignmentId: string,
  now = operationalNow(state),
): number {
  const sessions=state.sessions.filter(item=>item.assignmentId===assignmentId);
  if(!sessions.length)return 0;
  const starts=sessions.map(item=>item.start).sort(),ends=sessions.map(item=>item.end??now).sort();
  return intervalMinutes(starts[0],ends[ends.length-1],sessions.map(item=>({start:item.start,end:item.end??now})));
}
export function activeParticipants(state:Workspace,assignmentId:string):string[]{return [...new Set(state.sessions.filter(item=>item.assignmentId===assignmentId&&!item.end).flatMap(item=>item.personIds))];}
export function personActualMinutes(state:Workspace,personId:string,assignmentId?:string,at=operationalNow(state)){
  const sessions=state.sessions.filter(item=>item.personIds.includes(personId)&&(!assignmentId||item.assignmentId===assignmentId));
  if(!sessions.length)return 0;
  const starts=sessions.map(item=>item.start).sort(),ends=sessions.map(item=>item.end??at).sort();
  return intervalMinutes(starts[0],ends[ends.length-1],sessions.map(item=>({start:item.start,end:item.end??at})));
}
export function requiresBuddyAt(person:Person,at:string){return !!person.requiresBuddy&&(!person.buddyRestriction||person.buddyRestriction.from<=at&&(!person.buddyRestriction.until||person.buddyRestriction.until>at));}
export function requiresBuddyForInterval(person:Person,start:string,end:string){return !!person.requiresBuddy&&(!person.buddyRestriction||person.buddyRestriction.from<end&&(!person.buddyRestriction.until||person.buddyRestriction.until>start));}
export function requiredCrewSize(state:Workspace,assignment:Assignment,personIds:string[]=[],time:string|{start:string;end:string}=operationalNow(state),index?:AssignmentReadIndex){return Math.max(effectiveAssignmentRequirements(state,assignment,index).crewSize,personIds.some(id=>state.people.some(person=>person.id===id&&(typeof time==='string'?requiresBuddyAt(person,time):requiresBuddyForInterval(person,time.start,time.end))))?2:1);}
/** Rule workday constraints remain mandatory even when a booking-boundary exception is explicitly authorized. */
export function assignmentWorkingTimeAllowed(state:Workspace,assignment:Assignment,start:string,end?:string,index?:AssignmentReadIndex){
  const read=index?.state===state?index:undefined;
  const unit=read?read.units.get(assignment.unitId):state.units.find(item=>item.id===assignment.unitId),zone=state.properties.find(item=>item.id===unit?.propertyId)?.timeZone??'Europe/Helsinki';
  for(const occurrence of indexedOccurrences(state,assignment,read)){
    const rule=read?read.rules.get(ruleRevisionKey(occurrence.ruleId,occurrence.ruleRevision)):[...state.rules,...(state.ruleHistory??[])].find(item=>item.id===occurrence.ruleId&&item.revision===occurrence.ruleRevision);
    if(!rule)continue;
    const date=localDate(start,zone);
    if(rule.window.allowedWeekdays.length&&!rule.window.allowedWeekdays.includes(weekday(date)))return false;
    try{const from=localToInstant(date,rule.window.startTime,zone),to=localToInstant(date,rule.window.endTime,zone);if(start<from||start>=to||end&&end>to)return false;}catch{return false;}
  }
  return true;
}
export function executionConflicts(state:Workspace,assignmentId:string,personIds:string[],at=operationalNow(state)):Conflict[]{
  const allocation=committedAllocation(state,assignmentId),assignment=state.assignments.find(item=>item.id===assignmentId);
  if(!allocation||!assignment)return [{id:'execution-unallocated',code:'unallocated',severity:'blocker',message:'Start the committed shift before starting this work.',assignmentId}];
  const projected={...state,allocations:state.allocations.map(item=>item.id===allocation.id?{...item,participantIds:personIds}:item)};
  const result=assignmentConflicts(projected,assignmentId,at).filter(item=>item.severity==='blocker'&&!['availability','overlap','window-start','working-hours','crew'].includes(item.code));
  const add=(code:string,message:string,personId?:string)=>result.push({id:stableId('execution',assignmentId,code,personId??''),code,message,personId,assignmentId,shiftId:allocation.shiftId,severity:'blocker'});
  const window=effectiveAssignmentWindow(state,assignment),shift=state.shifts.find(item=>item.id===allocation.shiftId);
  if(window.earliestStart&&at<window.earliestStart)add('window-start','This work is not eligible to start yet.');
  if(!assignmentWorkingTimeAllowed(state,assignment,at))add('working-hours','The rule does not allow work at this local time.');
  if(!personIds.length)add('crew','Include a participant before starting work.');
  else if(personIds.length<requiredCrewSize(state,assignment,personIds,at))add('crew',`This service needs ${requiredCrewSize(state,assignment,personIds,at)} participant(s).`);
  for(const personId of personIds){
    if(!shift?.positions.some(item=>item.personId===personId&&item.coverageStart<=at&&item.coverageEnd>at))add('coverage','Every participant must be included in the current shift coverage.',personId);
    if(currentAttendance(state,personId,allocation.shiftId,at)!=='present')add('attendance','Mark every participant present before starting work.',personId);
    if(!state.availability.some(item=>item.personId===personId&&item.kind!=='unavailable'&&item.start<=at&&item.end>at)||state.availability.some(item=>item.personId===personId&&item.kind==='unavailable'&&item.start<=at&&item.end>at))add('availability','Current workday availability does not cover every participant. Update the calendar first.',personId);
    if(state.sessions.some(item=>!item.end&&item.assignmentId!==assignmentId&&item.personIds.includes(personId)))add('simultaneous-work','A participant already has work in progress. Pause it first.',personId);
  }
  return result;
}
export function assignmentConflicts(
  state: Workspace,
  assignmentId: string,
  now = operationalNow(state),
  index?: AssignmentReadIndex,
): Conflict[] {
  const read = index?.state === state ? index : undefined;
  const assignment = read ? read.assignments.get(assignmentId) : state.assignments.find((item) => item.id === assignmentId);
  if (
    !assignment ||
    ["completed", "declined", "cancelled"].includes(assignment.status)
  )
    return [];
  const unit = read ? read.units.get(assignment.unitId) : state.units.find((item) => item.id === assignment.unitId);
  const service = read ? read.services.get(assignment.serviceId) : state.services.find(
    (item) => item.id === assignment.serviceId,
  );
  const allocation = committedAllocation(state, assignmentId, read);
  const shift = state.shifts.find((item) => item.id === allocation?.shiftId);
  const window = effectiveAssignmentWindow(state, assignment, read);
  const requirements = effectiveAssignmentRequirements(state, assignment, read);
  const result: Conflict[] = [];
  const add = (
    code: string,
    message: string,
    severity: Conflict["severity"] = "warning",
    personId?: string,
  ) =>
    result.push({
      id: stableId("conflict", assignmentId, code, personId ?? ""),
      assignmentId,
      shiftId: allocation?.shiftId,
      unitId: unit?.id,
      personId,
      code,
      message,
      severity,
    });
  if (!unit || unit.archived)
    add("unit-unavailable", "The unit is missing or archived.", "blocker");
  if (unit && shift && unit.propertyId !== shift.propertyId)
    add(
      "property-mismatch",
      "The unit now belongs to another property. Review its shift commitment.",
      "blocker",
    );
  if (!service) add("service-missing", "Choose a valid service.", "blocker");
  if (service?.access === "vacant" && unit?.access !== "vacant")
    add(
      "access",
      unit?.access === "occupied"
        ? "This service requires confirmed vacancy."
        : "Confirm actual vacancy before starting this service.",
      "blocker",
    );
  if (
    service?.access === "vacant" &&
    unit?.access === "vacant" &&
    state.bookings.some(
      (item) =>
        item.unitId === unit.id &&
        item.status === "confirmed" &&
        item.arrival <= now &&
        item.arrival > (unit.accessUpdatedAt ?? unit.factUpdatedAt),
    )
  )
    add(
      "stale-access",
      "The vacancy confirmation predates a newer booking. Verify current access.",
      "blocker",
    );
  if (
    service?.access === "permission" &&
    unit?.access !== "vacant" &&
    !unit?.permissionGranted
  )
    add(
      "permission",
      "Record permission to enter the occupied unit.",
      "blocker",
    );
  if (
    assignment.dependsOn?.some(
      (id) =>
        (read ? read.assignments.get(id) : state.assignments.find((item) => item.id === id))?.status !==
        "completed",
    )
  )
    add("dependency", "Complete the prerequisite work first.", "blocker");
  if (
    assignment.occurrenceIds.some((id) =>
      (read ? read.occurrences.get(id)?.occurrence.status === 'needs-information' : state.occurrences.some(
        (item) => item.id === id && item.status === "needs-information",
      )),
    )
  )
    add(
      "needs-information",
      "Required source information is missing.",
      "blocker",
    );
  if (
    assignment.occurrenceIds.some((id) =>
      (read ? read.occurrences.get(id)?.occurrence.status === 'needs-scheduling' : state.occurrences.some(
        (item) => item.id === id && item.status === "needs-scheduling",
      )),
    )
  )
    add(
      "needs-scheduling",
      "No eligible working time exists before the deadline.",
      "blocker",
    );
  if (!assignment.date) add("undated", "Choose a workday for this assignment.");
  if (
    allocation?.start &&
    window.earliestStart &&
    allocation.start < window.earliestStart
  )
    add(
      "window-start",
      "Scheduled work starts before the eligible service window.",
      "blocker",
    );
  if (allocation?.end && window.dueAt && allocation.end > window.dueAt)
    add(
      "deadline",
      "Scheduled work finishes after the ready-by deadline.",
      "warning",
    );
  if(allocation?.start&&!assignmentWorkingTimeAllowed(state,assignment,allocation.start,allocation.end,read))add('working-hours','The planned interval is outside the rule’s allowed workdays or hours.','blocker');
  if (allocation && allocation.participantIds.length < requiredCrewSize(state,assignment,allocation.participantIds,allocation.start&&allocation.end?{start:allocation.start,end:allocation.end}:now,read))
    add(
      "crew",
      `This service needs ${requiredCrewSize(state,assignment,allocation.participantIds,allocation.start&&allocation.end?{start:allocation.start,end:allocation.end}:now,read)} participant(s).`,
      "blocker",
    );
  for (const personId of allocation?.participantIds ?? []) {
    const person = state.people.find((item) => item.id === personId);
    if (!person || person.archived)
      add(
        "person-missing",
        "A participant is missing or archived.",
        "blocker",
        personId,
      );
    if (
      requirements.requiredSkills.some(
        (skill) =>
          !person?.skills.some(
            (item) => item.toLocaleLowerCase() === skill.toLocaleLowerCase(),
          ),
      )
    )
      add(
        "skills",
        "A participant does not have every mandatory skill.",
        "blocker",
        personId,
      );
    if (currentAttendance(state, personId, allocation?.shiftId, now) === "absent")
      add("absent", "A participant is currently absent.", "blocker", personId);
    if (
      allocation?.start &&
      allocation.end &&
      availableMinutes(state, personId, allocation.start, allocation.end) <
        durationMinutes(allocation.start, allocation.end)
    )
      add(
        "availability",
        "A participant is not available for the scheduled interval.",
        "blocker",
        personId,
      );
    if (
      allocation?.start &&
      allocation.end &&
      state.allocations.some(
        (other) =>
          other.id !== allocation.id &&
          other.status === "committed" &&
          other.start &&
          other.end &&
          other.participantIds.includes(personId) &&
          overlap(allocation.start!, allocation.end!, other.start, other.end),
      )
    )
      add(
        "overlap",
        "This participant has another assignment at the same time.",
        "blocker",
        personId,
      );
  }
  return result;
}

export function conflictsForShift(
  state: Workspace,
  shiftId: string,
  at = operationalNow(state),
): Conflict[] {
  const shift = state.shifts.find((item) => item.id === shiftId);
  if (!shift || shift.status === "Completed" || shift.status === "Cancelled")
    return [];
  const result = shift.assignmentIds
    .flatMap((id) => assignmentConflicts(state, id, at))
    .map((item) => ({ ...item, shiftId }));
  for (const position of shift.positions) {
    const person = state.people.find((item) => item.id === position.personId);
    const add = (
      code: string,
      message: string,
      severity: Conflict["severity"] = "warning",
    ) =>
      result.push({
        id: stableId("staff", shift.id, position.id, code),
        code,
        message,
        severity,
        shiftId,
        personId: position.personId,
      });
    if (!position.personId) {
      add("unfilled", "A staffing position is unfilled.");
      continue;
    }
    if (!person || person.archived)
      add("archived-person", "This person is missing or archived.", "blocker");
    if (
      position.requiredSkills.some(
        (skill) =>
          !person?.skills.some(
            (item) => item.toLocaleLowerCase() === skill.toLocaleLowerCase(),
          ),
      )
    )
      add("position-skills", "Required position skills are not covered.");
    if (
      position.coverageStart < shift.start ||
      position.coverageEnd > shift.end ||
      position.coverageEnd <= position.coverageStart
    )
      add(
        "coverage-window",
        "Participant coverage must be contained in the shift.",
        "blocker",
      );
    if (
      availableMinutes(
        state,
        position.personId,
        position.coverageStart,
        position.coverageEnd,
      ) < durationMinutes(position.coverageStart, position.coverageEnd)
    )
      add(
        "coverage",
        "Workday intervals do not cover this participation window.",
      );
    if (currentAttendance(state, position.personId, shift.id, at) === "absent")
      add("absence", "This person is currently absent.");
    if (
      state.shifts.some(
        (other) =>
          other.id !== shift.id &&
          ["Planned", "Active"].includes(other.status) &&
          other.positions.some(
            (item) =>
              item.personId === position.personId &&
              overlap(
                position.coverageStart,
                position.coverageEnd,
                item.coverageStart,
                item.coverageEnd,
              ),
          ),
      )
    )
      add(
        "shift-overlap",
        "This person is committed to another shift during these hours.",
        "blocker",
      );
  }
  const estimate = staffingEstimate(state, shift, at);
  if (estimate.shortageMinutes > 0)
    result.push({
      id: `capacity-${shift.id}`,
      code: "capacity",
      message: `${Math.ceil(estimate.shortageMinutes)} labor minutes remain uncovered.`,
      severity: "warning",
      shiftId,
    });
  return result;
}

export function staffingEstimate(
  state: Workspace,
  shift: Shift,
  at = operationalNow(state),
): StaffingEstimate {
  const work = state.assignments.filter(
    (item) =>
      shift.assignmentIds.includes(item.id) &&
      !["declined", "cancelled", "completed"].includes(item.status),
  );
  let minimumCrew = 0;
  const laborMinutes = work.reduce((total, item) => {
    const requirements = effectiveAssignmentRequirements(state, item);
    const participants=activeParticipants(state,item.id);
    const allocation=committedAllocation(state,item.id);
    const timing=shift.status==='Active'?{start:at>shift.start?at:shift.start,end:shift.end}:allocation?.start&&allocation.end?{start:allocation.start,end:allocation.end}:{start:shift.start,end:shift.end};
    const crew=requiredCrewSize(state,item,participants.length?participants:allocation?.participantIds??[],timing);
    let remaining = item.remainingMinutes ?? requirements.estimateMinutes;
    if (["in-progress", "paused"].includes(item.status)) {
      // Explicit remaining work already accounts for closed sessions at the last pause.
      const sessions = state.sessions.filter(session => session.assignmentId === item.id && (item.remainingMinutes === undefined || !session.end));
      remaining = Math.max(0, remaining - sessions.reduce((minutes, session) => minutes + durationMinutes(session.start, session.end ?? at), 0));
    }
    if (remaining > 0) minimumCrew = Math.max(minimumCrew, crew);
    return total + remaining * crew;
  }, 0);
  const capacityStart = shift.status === "Active" && at > shift.start ? at : shift.start;
  // Distinct people and interval unions prevent double counting duplicate positions or split availability.
  const people = [
    ...new Set(
      shift.positions
        .map((item) => item.personId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  let productive = 0;
  for (const personId of people) {
    const person = state.people.find(item => item.id === personId);
    if (!person || person.archived || currentAttendance(state, personId, shift.id, at) === "absent") continue;
    const coverage = shift.positions.filter(
      (item) => item.personId === personId,
    );
    const intervals = state.availability
      .filter(
        (item) => item.personId === personId && item.kind !== "unavailable",
      )
      .flatMap((interval) =>
        coverage
          .filter((position) =>
            overlap(
              interval.start,
              interval.end,
              position.coverageStart,
              position.coverageEnd,
            ),
          )
          .map((position) => ({
            start:
              interval.start > position.coverageStart
                ? interval.start
                : position.coverageStart,
            end:
              interval.end < position.coverageEnd
                ? interval.end
                : position.coverageEnd,
          })),
      );
    const blocked = [
      ...state.availability.filter(item => item.personId === personId && item.kind === "unavailable"),
      ...state.shifts.filter(item => item.id !== shift.id && ["Planned", "Active"].includes(item.status))
        .flatMap(item => item.positions.filter(position => position.personId === personId)
          .map(position => ({ start: position.coverageStart, end: position.coverageEnd }))),
      ...state.allocations.filter(item => item.shiftId !== shift.id && item.status === "committed" && item.participantIds.includes(personId) && item.start && item.end)
        .map(item => ({ start: item.start!, end: item.end! })),
    ];
    productive += netIntervalMinutes(capacityStart, shift.end, intervals, blocked);
  }
  const reserved = Math.min(productive, Math.max(0, work.length - 1) * 5);
  const available = Math.max(0, productive - reserved);
  return {
    laborMinutes,
    availableMinutes: available,
    shortageMinutes: Math.max(0, laborMinutes - available),
    suggestedPeople: Math.max(
      minimumCrew,
      Math.ceil(laborMinutes / Math.max(1, durationMinutes(shift.start, shift.end) - 30)),
    ),
    unfilledPositions: shift.positions.filter((item) => !item.personId).length,
    assumptions: [
      "Estimates use service duration × required crew, without automatic pair speedup.",
      "Availability gaps and overlapping duplicate positions are excluded.",
      "Unavailable periods and commitments in other shifts are excluded.",
      "For active shifts, capacity and estimated labor use the remaining time.",
      "Suggestion reserves 30 minutes per full shift; capacity includes five minutes between assignments. Scheduling still needs review.",
    ],
  };
}

export interface CandidateRow {
  assignment: Assignment;
  unit: Unit;
  service: ServiceDefinition;
  group: 'day'|'carryover'|'backlog';
  window: {earliestStart?:string;dueAt?:string};
  accessibleNow: boolean;
  attention: string[];
  ownerLabel?: string;
  committedShiftId?: string;
}
/** A read projection: prior work and neutral extras remain visible without changing their dates or ownership. */
export function planningCandidates(state:Workspace,options:{propertyId:string;date:string;territory?:string;access?:'available'|'attention';search?:string;selectedIds?:string[];at?:string}):CandidateRow[]{
  const at=options.at??operationalNow(state),selected=new Set(options.selectedIds??[]);
  const search=options.search?.toLocaleLowerCase(),assignments=firstById(state.assignments),people=firstById(state.people);
  const units=new Map(state.units.map(item=>[item.id,item])),services=new Map(state.services.map(item=>[item.id,item]));
  const occurrences=new Map(state.occurrences.map(item=>[item.id,item]));
  const current=new Map<string,Assignment>();
  for(const impact of [...state.impacts].sort((a,b)=>a.sourceRevision-b.sourceRevision))if(impact.assignmentId&&impact.action==='update-window'&&impact.status!=='superseded'&&impact.proposedAssignment)current.set(impact.assignmentId,impact.proposedAssignment);
  const committed=new Map(state.allocations.filter(item=>item.status==='committed').map(item=>[item.assignmentId,item]));
  const lastArrival=new Map<string,string>();for(const booking of state.bookings)if(booking.status==='confirmed'&&booking.arrival<=at&&booking.arrival>(lastArrival.get(booking.unitId)??''))lastArrival.set(booking.unitId,booking.arrival);
  const handovers=new Map((state.handovers??[]).filter(item=>item.status==='open').map(item=>[item.assignmentId,item]));
  const rows:CandidateRow[]=[];
  for(const assignment of state.assignments){
    const unit=units.get(assignment.unitId),service=services.get(assignment.serviceId);
    if(!unit||!service||unit.archived||unit.propertyId!==options.propertyId||options.territory&&unit.territory!==options.territory)continue;
    if(!selected.has(assignment.id)&&(!['open','paused'].includes(assignment.status)||assignment.date&&assignment.date>options.date))continue;
    if(search&&!`${unit.name} ${service.name} ${unit.territory}`.toLocaleLowerCase().includes(search))continue;
    const facts=assignment.occurrenceIds.map(id=>occurrences.get(id)).filter((item):item is NonNullable<typeof item>=>!!item&&!['superseded','suppressed'].includes(item.status));
    const proposed=current.get(assignment.id),starts=facts.flatMap(item=>item.earliestStart?[item.earliestStart]:[]),ends=facts.flatMap(item=>item.dueAt?[item.dueAt]:[]);
    if(assignment.manualOverrides?.earliestStart)starts.push(assignment.manualOverrides.earliestStart);
    if(assignment.manualOverrides?.dueAt)ends.push(assignment.manualOverrides.dueAt);
    const window={earliestStart:starts.sort().slice(-1)[0]??proposed?.earliestStart??assignment.earliestStart,dueAt:ends.sort()[0]??proposed?.dueAt??assignment.dueAt};
    const attention:string[]=[];
    if(service.access==='vacant'&&unit.access!=='vacant')attention.push(unit.access==='occupied'?'The unit is occupied.':'Current access is not confirmed.');
    if(service.access==='permission'&&unit.access!=='vacant'&&!unit.permissionGranted)attention.push('Permission to enter is not recorded.');
    if(service.access!=='none'&&(lastArrival.get(unit.id)??'')>(unit.accessUpdatedAt??unit.factUpdatedAt))attention.push('Access information predates the latest arrival.');
    if(window.earliestStart&&window.earliestStart>at)attention.push('The service window has not opened.');
    if(facts.some(item=>['needs-information','needs-scheduling'].includes(item.status)))attention.push('Work requirements need review.');
    if(assignment.dependsOn?.some(id=>assignments.get(id)?.status!=='completed'))attention.push('Prerequisite work is unfinished.');
    const accessibleNow=!attention.length&&['open','paused'].includes(assignment.status);
    if(options.access==='available'&&!accessibleNow||options.access==='attention'&&!attention.length)continue;
    const handover=handovers.get(assignment.id),owner=handover?.ownerId?people.get(handover.ownerId):undefined;
    rows.push({assignment,unit,service,group:assignment.date===null?'backlog':assignment.date<options.date?'carryover':'day',window,accessibleNow,attention,ownerLabel:handover?(owner?.name??handover.ownerLabel??'Unassigned'):undefined,committedShiftId:committed.get(assignment.id)?.shiftId});
  }
  return rows;
}
export function outstandingHandovers(state:Workspace,options:{propertyId?:string;date?:string;shiftId?:string}={}){
  return (state.handovers??[]).filter(item=>{
    const assignment=state.assignments.find(work=>work.id===item.assignmentId),unit=state.units.find(value=>value.id===assignment?.unitId),shift=state.shifts.find(value=>value.id===item.fromShiftId);
    if(item.status==='resolved'||!assignment||['completed','declined','cancelled'].includes(assignment.status))return false;
    const propertyId=unit?.propertyId??shift?.propertyId,zone=state.properties.find(value=>value.id===propertyId)?.timeZone??'Europe/Helsinki',date=item.dueAt?localDate(item.dueAt,zone):shift?.date;
    return (!options.propertyId||propertyId===options.propertyId)&&(!options.shiftId||item.fromShiftId===options.shiftId)&&(!options.date||!date||date<=options.date);
  });
}

export function bookingConflicts(
  state: Workspace,
  unitId?: string,
): Conflict[] {
  const result: Conflict[] = [];
  const bookings = state.bookings.filter(
    (item) =>
      item.status === "confirmed" && (!unitId || item.unitId === unitId),
  );
  for (let index = 0; index < bookings.length; index++)
    for (const other of bookings.slice(index + 1))
      if (
        bookings[index].unitId === other.unitId &&
        overlap(
          bookings[index].arrival,
          bookings[index].departure,
          other.arrival,
          other.departure,
        )
      )
        result.push({
          id: stableId("booking", bookings[index].id, other.id),
          unitId: other.unitId,
          code: "booking-overlap",
          severity: "warning",
          message:
            "Source bookings overlap. Actual vacancy remains unconfirmed.",
        });
  return result;
}

export function effectiveAssignmentWindow(
  state: Workspace,
  assignment: Assignment,
  index?: AssignmentReadIndex,
) {
  const occurrences = indexedOccurrences(state, assignment, index);
  const starts = occurrences
    .map((item) => item.earliestStart)
    .filter((item): item is string => Boolean(item));
  const deadlines = occurrences
    .map((item) => item.dueAt)
    .filter((item): item is string => Boolean(item));
  if (assignment.manualOverrides?.earliestStart)
    starts.push(assignment.manualOverrides.earliestStart);
  if (assignment.manualOverrides?.dueAt)
    deadlines.push(assignment.manualOverrides.dueAt);
  starts.sort();
  return {
    earliestStart: starts.length
      ? starts[starts.length - 1]
      : assignment.earliestStart,
    dueAt: deadlines.length ? deadlines.sort()[0] : assignment.dueAt,
  };
}

export function effectiveAssignmentRequirements(
  state: Workspace,
  assignment: Assignment,
  index?: AssignmentReadIndex,
) {
  const read = index?.state === state ? index : undefined;
  const current = read ? read.currentRequirements.get(assignment.id) : state.impacts
    .filter(
      (item) =>
        item.assignmentId === assignment.id &&
        item.action === "update-window" &&
        item.status !== "superseded" &&
        item.proposedAssignment,
    )
    .sort((a, b) => b.sourceRevision - a.sourceRevision)[0]?.proposedAssignment;
  const occurrences = indexedOccurrences(state, assignment, read);
  const requirementFor = (occurrence: (typeof occurrences)[number]) => {
    const rule = read ? read.rules.get(ruleRevisionKey(occurrence.ruleId, occurrence.ruleRevision)) : [...state.rules, ...(state.ruleHistory ?? [])].find(
      (item) =>
        item.id === occurrence.ruleId &&
        item.revision === occurrence.ruleRevision,
    );
    const service = read ? read.services.get(occurrence.serviceId) : state.services.find(
      (item) => item.id === occurrence.serviceId,
    );
    return {
      requiredSkills:
        occurrence.requiredSkills ??
        rule?.requiredSkills ??
        service?.requiredSkills ??
        [],
      crewSize: occurrence.crewSize ?? rule?.crewSize ?? service?.crewSize ?? 1,
    };
  };
  const requirements = occurrences.map(requirementFor);
  return {
    requiredSkills: [
      ...new Set([
        ...(current?.requiredSkills ?? assignment.requiredSkills),
        ...requirements.flatMap((item) => item.requiredSkills),
      ]),
    ],
    crewSize: Math.max(
      current?.crewSize ?? assignment.crewSize,
      ...requirements.map((item) => item.crewSize),
    ),
    estimateMinutes: assignment.estimateProvenance?.confidence==='unrecorded'?assignment.estimateMinutes:current?.estimateMinutes ?? (assignment.estimateSource&&assignment.estimateSource!=='fixed'&&!['completed','in-progress','paused'].includes(assignment.status)?resolveEstimate(state,{unitId:assignment.unitId,serviceId:assignment.serviceId,assignment}).minutes:assignment.estimateMinutes),
  };
}

export function syncEligibility(
  state: Workspace,
  assignmentId: string,
  now = operationalNow(state),
): { status: "eligible" | "held" | "stale"; reason: string } {
  const assignment = state.assignments.find((item) => item.id === assignmentId);
  const unit = state.units.find((item) => item.id === assignment?.unitId);
  const policy = state.integrationPolicies.hotellinx;
  if (!assignment || !unit || assignment.status !== "completed")
    return {
      status: "held",
      reason: "Only completed service facts can be synchronized.",
    };
  if(!assignment.completedAt||!Number.isFinite(Date.parse(assignment.completedAt)))return {status:'held',reason:'The completion time is unrecorded. Review the historical work before synchronizing it.'};
  const cycle=cycleForAssignment(state,assignment);
  if(cycle&&assignment.stayCycleRevision!==undefined&&assignment.stayCycleRevision!==cycle.serviceRevision)return {status:'stale',reason:'A manually registered guest-return cleaning superseded this completion.'};
  const service = state.services.find(
    (item) => item.id === assignment.serviceId,
  );
  if (!service || service.readinessEffect === "none")
    return {
      status: "held",
      reason:
        "This service does not produce a cleaning or guest-readiness fact.",
    };
  if (
    service.readinessEffect === "inspected" &&
    (assignment.completionResult === "failed" || unit.cleanliness !== "ready")
  )
    return {
      status: "held",
      reason:
        "Guest-ready release requires a passed inspection and current readiness.",
    };
  const arrived = state.bookings.filter(
    (item) =>
      item.unitId === unit.id &&
      item.status === "confirmed" &&
      item.arrival <= now && !cycle?.bookingIds.includes(item.id),
  );
  if (
    assignment.completedAt &&
    arrived.some((item) => item.arrival > assignment.completedAt!)
  )
    return {
      status: "stale",
      reason:
        "A newer stay began after this completion. The old clean state must not be sent.",
    };
  if (!policy.enabled || policy.cleaningSync === "never")
    return { status: "held", reason: "Synchronization is disabled by policy." };
  if (policy.cleaningSync === 'checked-out' && (unit.access!=='vacant' || !cycle || !cycle.checkout || cycle.checkout.serviceRevision!==cycle.serviceRevision || cycle.checkout.sourceFingerprint!==cycle.sourceFingerprint || !currentFulfillment(state,cycle,assignment.id)))
    return {
      status: "held",
      reason: "Confirmed checkout is required for the relevant current stay.",
    };
  return {
    status: "eligible",
    reason:
      "The current service cycle and synchronization policy allow this update.",
  };
}
